/**
 * Offboarding request list — `GET offboarding/requests/`, scoped to the
 * caller (a tenant sees only their own offboarding; managers see whatever
 * `buildScope` grants them). No server-side filters on this endpoint, unlike
 * maintenance's status/priority — just pagination.
 *
 * "Start offboarding" links to the `initiate` workflow entry point, not a
 * plain CRUD create form — see the backend route file's top comment.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { listOffboardingRequests } from '../../api/endpoints/offboarding';

export function OffboardingListPage(): React.JSX.Element {
  const { user } = useAuth();
  const canManage = user?.role === 'LANDLORD' || user?.role === 'AGENT' || user?.role === 'SUPERAGENT';
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['offboarding-requests', { page }],
    queryFn: () => listOffboardingRequests({ page }),
  });

  const requests = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  return (
    <section>
      <h2>Offboarding</h2>

      {canManage && (
        <p>
          <Link to="initiate">Start offboarding</Link>
        </p>
      )}

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && requests.length === 0 && (
        <EmptyState
          icon={LogOut}
          message="No offboarding requests found."
          action={canManage ? { label: 'Start offboarding', to: 'initiate' } : undefined}
        />
      )}

      {query.isSuccess && requests.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {requests.map((request) => (
              <li key={request.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Tenant</span>
                  <span className="record-card__value">
                    <Link to={`${request.id}`}>{request.tenant_name}</Link>
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Unit</span>
                  <span className="record-card__value">{request.unit_number}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Property</span>
                  <span className="record-card__value">{request.property_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value">{request.status}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Initiated</span>
                  <span className="record-card__value">{request.initiated_at}</span>
                </div>
                <div className="record-card__actions">
                  <Link to={`${request.id}`} className="button button--outlined">
                    <ChevronRight size={14} className="icon-inline" aria-hidden="true" />
                    View
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Unit</th>
                <th>Property</th>
                <th>Status</th>
                <th>Initiated</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <Link to={`${request.id}`}>{request.tenant_name}</Link>
                  </td>
                  <td>{request.unit_number}</td>
                  <td>{request.property_name}</td>
                  <td>{request.status}</td>
                  <td>{request.initiated_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default OffboardingListPage;
